import { useMemo, useState } from "react";
import TableComponent from "../../components/common/TableComponent";
import PaginationComponent from "../../components/PaginationComponent";
import Loading from "../../components/common/LoaderCommon";
import {
  useInfiniteGetPaymentReport,
  useDownloadPaymentReport,
} from "../../service/reportsService";
import DownloadButton from "./DownloadFile";
import { ReusableCombobox } from "../../utils/components/ui/combobox";
import { useInfiniteGetAllCustomer } from "../../service/customerService";
import { X } from "lucide-react";

type Props = {};

function PaymentReport({}: Props) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false);

  const downloadMutation = useDownloadPaymentReport();

  const { data, fetchNextPage, isLoading } = useInfiniteGetPaymentReport({
    pageSize: 10,
    pageNumber: 0,
    clientId: selectedCustomer,
    startDate,
    endDate,
  });

  const { data: customerData } = useInfiniteGetAllCustomer({
    pageSize: 10,
    pageNo: 0,
    customerName: customerSearch,
  });

  const totalPages = Math.ceil((Number(data?.pages[0]?.totalPages) || 0) / 10);

  const paymentData = data?.pages[0]?.paymentReportPdfResponseList || [];

  const customerOptions =
    customerData?.pages
      ?.flatMap((page) => page.customerResponse || [])
      .map((customer: any) => ({
        value: customer.id,
        label: customer.username,
      })) || [];

  const paymentColumns = useMemo(
    () => [
      { header: "Invoice Number", accessor: "invoiceNumber" },
      { header: "Customer Name", accessor: "customerName" },
      { header: "Payment Date", accessor: "paymentDate" },
      { header: "Amount", accessor: "amount" },
      { header: "Payment Mode", accessor: "paymentMode" },
      { header: "Customer Email", accessor: "customerEmail" },
      { header: "Customer Phone", accessor: "customerPhone" },
      { header: "Customer Address", accessor: "customerAddress" },
    ],
    [],
  );

  const isFilterActive = !!startDate || !!endDate || !!selectedCustomer;

  const handleResetFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedCustomer("");
    setCustomerSearch("");
    setCurrentPage(0);
  };

  return (
    <div className="space-y-4">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex justify-between items-end flex-wrap gap-4">
          <div className="flex gap-4 flex-wrap items-end">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                Customer
              </label>
              <div className="min-w-[220px]">
                <ReusableCombobox
                  options={customerOptions}
                  value={selectedCustomer}
                  onValueChange={(value) => {
                    setSelectedCustomer(value as string);
                  }}
                  open={customerComboboxOpen}
                  setOpen={setCustomerComboboxOpen}
                  placeholder="Select Customer"
                  searchPlaceholder="Search customer..."
                  emptyText="No customers found."
                  className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
                  onInputChange={(val) => setCustomerSearch(val)}
                />
              </div>
            </div>
            {isFilterActive && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors self-end"
              >
                <X className="w-4 h-4" />
                Reset
              </button>
            )}
          </div>
          <DownloadButton
            endDate={endDate}
            startDate={startDate}
            mutation={downloadMutation}
          />
        </div>
      </div>

      {/* Table card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
        <div className="flex justify-between items-center border-b border-[#E8EEF9] py-3 px-6">
          <p className="text-sm text-[#52627C]">
            <span className="font-semibold text-[#21314A]">
              {paymentData?.length}
            </span>{" "}
            Payment report entries found
          </p>
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
            fetchNextPage={fetchNextPage}
            fetchPreviousPage={() => {
              if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
              }
            }}
          />
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <TableComponent
            caption="A list of payment report entries."
            columns={paymentColumns}
            data={paymentData || []}
          />
        )}
      </div>
    </div>
  );
}

export default PaymentReport;
